import { FC, useCallback, useState } from 'react';
import Modal from 'src/common/components/ModalDS22';
import { useTranslation } from 'next-i18next';
import Button from 'src/common/components/ButtonDS22';
import Typography from 'src/common/components/Typography';
import broadcastNotification from 'src/common/components/Notification/broadcastNotification';
import { EditorListing } from 'src/common/types/api/listings';
import styles from './ApprovalModal.module.scss';
import bulkSubmitListingsForReview from '../../utils/api/submitListingForReview';

export type Props = {
  listings: Pick<EditorListing, 'id' | 'shortname'>[];
  isOpen: boolean;
  close: () => void;
  afterSubmit?: () => void;
};

const {
  HeaderDS22: Header,
  BodyDS22: Body,
  ControllerDS22: Controller,
  FooterDS22: Footer,
} = Modal;

const ApprovalModal: FC<Props> = ({
  listings,
  isOpen,
  close,
  afterSubmit,
}) => {
  const [t] = useTranslation('admin-listings');
  const [isLoading, setLoading] = useState(false);
  const hasMultipleListings = listings?.length > 1;
  const onReviewSubmit = useCallback(async () => {
    const ids = listings?.map((listing) => listing.id);
    setLoading(true);
    const reviewSubmitted = await bulkSubmitListingsForReview(ids);
    if (reviewSubmitted) {
      broadcastNotification({
        i18nKey: 'listing-editor--notify--review-submitted',
        translation: 'admin-listings',
        variant: 'success',
        translationOption: { count: listings?.length },
      });
      afterSubmit?.();
    } else {
      broadcastNotification({
        i18nKey: 'listing-editor--notify--review-error',
        translation: 'admin-listings',
        variant: 'error',
        translationOption: { count: listings?.length },
      });
    }
    setLoading(false);
    close();
  }, [afterSubmit, close, listings]);

  return (
    <Modal
      modalOpen={isOpen}
      closeModal={close}
      position="middle"
      label={t('listing-editor--ready-to-publish-title')}
    >
      <Controller
        showBackText
        onBack={close}
      />
      <Header
        title={t('listing-editor--ready-to-publish-title')}
        titleSize="heading-4"
      />
      <Body
        slot1={(
          <div className={styles.content}>
            <Typography
              type="p"
              size="body-m"
            >
              {t('listing-editor--ready-to-publish-description', { count: listings.length })}
            </Typography>
            {hasMultipleListings && (
              <div className={styles.multipleListings}>
                <Typography
                  type="p"
                  size="body-m"
                >
                  {t('listing-editor--list-of-listings')}
                </Typography>
                <ul className={styles.list}>
                  {listings.map((listing) => (
                    <li key={listing.id}>
                      <Typography
                        type="p"
                        size="body-m"
                      >
                        {listing.shortname}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        spacingBottom="s"
      />
      <Footer
        fullWidth
        buttonPrimary={(
          <Button
            onClick={onReviewSubmit}
            loading={isLoading}
          >
            {t('listing-editor--submit-for-review-cta')}
          </Button>
        )}
        buttonSecondary={(
          <Button
            variant="secondary"
            onClick={close}
          >
            {t('listing-editor--keep-editing-cta')}
          </Button>
        )}
      />
    </Modal>
  );
};

export default ApprovalModal;
